/**
 * What the SoundCloud sign-in keeps in this tab between `auth/begin` and the return page
 * (`/app/catalog/connect/soundCloud?state=<sessionUuid>`). MusicAPI gives the session uuid back in
 * `state`, so only the artist is kept: the route of `auth/complete` names the account, and the
 * server refuses a session of another account. Per tab: `sessionStorage`. Every access can throw
 * (a private window, storage that is off); then the return page uses the active artist.
 */
const SESSION_KEY = 'm24SoundCloudConnect';
/** The Catalog card shows the error line of a failed connect until the next try (design a2 state 6). */
const FAILED_KEY = 'm24SoundCloudConnectFailed';

interface StoredConnect {
  sessionUuid: string;
  accountId: number;
}

export function rememberSoundCloudConnect(sessionUuid: string, accountId: number): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ sessionUuid, accountId }));
    window.sessionStorage.removeItem(FAILED_KEY);
  } catch {
    // The return page falls back to the active artist.
  }
}

/** The artist of the sign-in with this session uuid, or null when this tab did not start it. */
export function soundCloudConnectAccount(sessionUuid: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw === null) {
      return null;
    }
    const stored = JSON.parse(raw) as Partial<StoredConnect>;
    return stored.sessionUuid === sessionUuid && typeof stored.accountId === 'number' ? stored.accountId : null;
  } catch {
    return null;
  }
}

export function forgetSoundCloudConnect(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to forget.
  }
}

export function markSoundCloudConnectFailed(failed: boolean): void {
  try {
    if (failed) {
      window.sessionStorage.setItem(FAILED_KEY, '1');
    } else {
      window.sessionStorage.removeItem(FAILED_KEY);
    }
  } catch {
    // The card then shows no error line.
  }
}

export function isSoundCloudConnectFailed(): boolean {
  try {
    return window.sessionStorage.getItem(FAILED_KEY) === '1';
  } catch {
    return false;
  }
}
